import { Card } from '../../components/ui/Card.js';
import { useFetch } from '../../hooks/useFetch.js';
import { RENTAL_ZONE } from '../../utils/mytTime.js';

/**
 * 358 (#1091, FR-001–FR-005): the customer's credit, the gift certificates staff issued to their verified email,
 * added up. Customers read "Credit" (the PO found "gift certificate" misplaced); staff keep "Gift certificates".
 * The lines are in the order a booking draws from them (soonest expiry first), the same order as checkout.
 */
interface CreditResponse {
  verified: boolean;
  total_minor: number;
  items: Array<{ id: string; code: string; balance_minor: number; expires_at: string | null }>;
}

const rm = (minor: number) => `RM ${(minor / 100).toFixed(2)}`;

/** "10 Nov 2026", the Malaysian day the certificate stops working. */
const expiryDay = (iso: string) =>
  new Date(iso).toLocaleDateString('en-GB', {
    timeZone: RENTAL_ZONE,
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });

export function CreditCard() {
  const read = useFetch<CreditResponse>('/credit');

  let body: preact.JSX.Element | null = null;
  if (read.status === 'ok' && read.data) {
    const credit = read.data;
    if (!credit.verified) {
      body = (
        <p data-testid="credit-unverified" className="text-sm text-gray-400">
          Verify your email address to see credit sent to it.
        </p>
      );
    } else if (credit.items.length === 0) {
      body = (
        <p data-testid="credit-empty" className="text-sm text-gray-400">
          You have no credit.
        </p>
      );
    } else {
      body = (
        <>
          <p data-testid="credit-total" className="mb-2 text-2xl font-bold text-primary">
            {rm(credit.total_minor)}
          </p>
          <ul className="divide-y divide-white/5">
            {credit.items.map((item) => (
              <li
                key={item.id}
                data-testid="credit-item"
                className="flex items-center justify-between gap-3 py-2 text-sm"
              >
                <span className="font-mono text-gray-400">{item.code}</span>
                <span className="flex flex-col items-end">
                  <span className="font-semibold text-text">{rm(item.balance_minor)}</span>
                  <span className="text-xs text-gray-500">
                    {item.expires_at ? `Expires ${expiryDay(item.expires_at)}` : 'No expiry'}
                  </span>
                </span>
              </li>
            ))}
          </ul>
        </>
      );
    }
  } else if (read.status !== 'idle' && read.status !== 'loading') {
    body = (
      <p className="flex items-center justify-between gap-3 text-sm">
        <span data-testid="credit-failed" className="text-error">
          Your credit could not be loaded.
        </span>
        {/* A retry only where retrying can succeed (frontend rule): never for an ended session or a refusal. */}
        {read.status === 'failed' ? (
          <button type="button" onClick={read.retry} className="text-primary underline">
            Try again
          </button>
        ) : null}
      </p>
    );
  }

  return (
    <Card className="mb-6">
      <div data-testid="credit-card">
        <h2 className="mb-1 text-sm font-semibold text-text">Credit</h2>
        {body}
      </div>
    </Card>
  );
}
